import { Component, computed, inject, input, model } from "@angular/core";
import { FileMeta } from "../job-type-config";
import { rxResource } from "@angular/core/rxjs-interop";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { Job } from "../../../../models/job";
import { MonacoDiffEditorComponent } from "../../../../shared/monaco-diff-editor/monaco-diff-editor.component";
import { MonacoEditorComponent } from "../../../../shared/monaco-editor/monaco-editor.component";
import { lintEnvSh, lintRunfile } from "../../../../shared/monaco-editor/editor-lint";
import { RUNFILE_LANGUAGE } from "../../../../shared/monaco-editor/monaco-setup";
import { NzAlertModule } from "ng-zorro-antd/alert";
import { NzCardComponent } from "ng-zorro-antd/card";
import { NzTabsModule } from "ng-zorro-antd/tabs";


interface TemplateFileResponse {
  path: string
  content: string
}

@Component({
  selector: "app-file-editor",
  templateUrl: "./file-editor.component.html",
  styleUrl: "./file-editor.component.less",
  imports: [MonacoDiffEditorComponent, MonacoEditorComponent, NzAlertModule, NzCardComponent, NzTabsModule],
})
export class FileEditorComponent {
  private cachedProjectService = inject(CachedProjectService);

  readonly runfile = input<FileMeta | null>(null);
  readonly job = input<Job | null>(null);

  readonly editedRunfileContent = model<string>("");
  readonly editedEnvShContent = model<string>("");

  protected readonly runfileLanguage = RUNFILE_LANGUAGE;
  protected readonly runfileMarkers = computed(() =>
    lintRunfile(this.editedRunfileContent()),
  );
  protected readonly envShMarkers = computed(() =>
    lintEnvSh(this.editedEnvShContent()),
  );

  protected runfileResource = rxResource<TemplateFileResponse, {id: string, runfile: string}>({
    params: computed(() => {
      const job = this.job();
      const runfile = this.runfile();
      if (job && runfile) {
        return { id: job.id, runfile: runfile.path };
      }
      return undefined;
    }),
    stream: ({ params }) =>
      this.cachedProjectService.getRunfileFromPath(params.id, params.runfile),
  });

  /** Non-null only when the error is something other than a missing file (404). */
  protected envShUnexpectedError = computed(() => {
    const err: any = this.envShResource.error();
    return err && err.status !== 404 ? err : null;
  });

  protected envShResource = rxResource<TemplateFileResponse, {id: string}>({
    params: computed(() => {
      const job = this.job();
      return job ? { id: job.id } : undefined;
    }),
    stream: ({ params }) =>
      this.cachedProjectService.getJobEnvSh(params.id),
  });
}
